/**
 * Authentication Context and Business Session Management
 * Provides centralized auth state and business context for the entire app
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import http from '../services/httpClient';
import PermissionService from '../services/permissionService';
import { primeBusinessName, clearCachedBusinessName } from '../utils/businessIdentity';

// Create Auth Context
const AuthContext = createContext(null);

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [businessId, setBusinessId] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state from localStorage
  useEffect(() => {
    try {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      const storedBusinessId = localStorage.getItem('businessId');
      
      if (storedToken && storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          setToken(storedToken);
          setUser(userData);
          
          // Use stored business ID or user data business ID
          const userBusinessId = userData?.businessId || userData?.business_id || userData?.businessid;
          const businessIdToUse = storedBusinessId || userBusinessId;
          
          if (businessIdToUse) {
            setBusinessId(businessIdToUse);
            // Ensure business ID is stored in localStorage
            if (!storedBusinessId) {
              localStorage.setItem('businessId', businessIdToUse.toString());
            }
          }
          
          setIsAuthenticated(true);
          // Sign-in never returns the business name; warm it so report covers
          // read "Acme Cafe (BID 136)" instead of a bare "BID 136".
          primeBusinessName();
        } catch (parseError) {
          console.error('Error parsing stored user data:', parseError);
          // Clear corrupted data
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setIsAuthenticated(false);
        }
      } else if (storedBusinessId) {
        // Keep business ID even if not logged in
        setBusinessId(storedBusinessId);
      }
    } catch (error) {
      console.error('Error initializing auth state:', error);
      // Clear corrupted data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Login function
  const login = useCallback(async (email, password, rememberMe = false) => {
    try {
      const response = await http.post('/auth/signin', { email, password, rememberMe });

      // Handle different response formats
      const data = response.data || response;
      
      // Extract token and user data
      const newToken = data.token || data.accessToken;
      const userData = data.user || data;

      if (!newToken) {
        throw new Error('No token received from server');
      }

      // Normalize QR billing property name (backend uses hasQRBilling, frontend uses isQRBilling)
      if (userData.hasQRBilling !== undefined && userData.isQRBilling === undefined) {
        userData.isQRBilling = userData.hasQRBilling;
      }

      // Store auth data
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Store business ID separately for easier access
      // Try multiple possible field names
      const userBusinessId = userData.businessId || userData.business_id || userData.businessid;
      if (userBusinessId) {
        localStorage.setItem('businessId', userBusinessId.toString());
        setBusinessId(userBusinessId);
      } else {
        console.warn('⚠️ No businessId in login response');
      }
      
      // Store user ID separately for WebSocket registration
      const userIdValue = userData.userId || userData.user_id || userData.id;
      if (userIdValue) {
        localStorage.setItem('userId', userIdValue.toString());
      } else {
        console.warn('⚠️ No userId found in login response');
      }

      // Update state
      setToken(newToken);
      setUser(userData);
      setIsAuthenticated(true);

      // Sign-in never returns the business name; fetch it once now so the first
      // report export doesn't have to wait on the network.
      primeBusinessName();

      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      
      // Provide user-friendly error messages
      if (error.status === 401) {
        throw new Error('Invalid email or password');
      } else if (error.status === 403) {
        throw new Error('Please verify your email before signing in');
      } else if (error.message) {
        throw error;
      } else {
        throw new Error('Login failed. Please try again.');
      }
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      // Call backend logout endpoint using httpClient
      if (token) {
        await http.post('/auth/signout');
      }
    } catch (error) {
      console.warn('Logout API call failed (continuing):', error);
    } finally {
      // Clear local state regardless of API success
      const bizId = localStorage.getItem('businessId');
      if (bizId) {
        localStorage.removeItem(`onboarding_profile_draft_${bizId}`);
      }
      localStorage.removeItem('onboarding_profile_draft');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('businessId');
      localStorage.removeItem('userId');
      localStorage.removeItem('loginBackgroundImage');
      localStorage.removeItem('rememberedEmail'); // Clear Remember Me email on logout
      // Drop the cached name so the next account's reports don't inherit it.
      clearCachedBusinessName();


      // Clear permissions cache
      PermissionService.clearCache();
      
      setToken(null);
      setUser(null);
      setBusinessId(null);
      setIsAuthenticated(false);
      
    }
  }, [token]);

  // Get auth headers for API calls
  const getAuthHeaders = useCallback(() => {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Note: business_id now comes from Redis session automatically
    // No need to manually include x-business-id header

    return headers;
  }, [token]);

  // Google Sign-In function
  const googleSignIn = useCallback(async (credential) => {
    try {
      const response = await http.post('/auth/google', { credential });

      // Handle response
      const data = response.data || response;
      
      // Extract token and user data
      const newToken = data.accessToken || data.token;
      const userData = data.user || data;

      if (!newToken) {
        throw new Error('No token received from Google Sign-In');
      }

      // Normalize QR billing property name (backend uses hasQRBilling, frontend uses isQRBilling)
      if (userData.hasQRBilling !== undefined && userData.isQRBilling === undefined) {
        userData.isQRBilling = userData.hasQRBilling;
      }

      // Store auth data
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Store business ID
      const userBusinessId = userData.businessId || userData.business_id || userData.businessid;
      if (userBusinessId) {
        localStorage.setItem('businessId', userBusinessId.toString());
        setBusinessId(userBusinessId);
      }
      
      // Store user ID for WebSocket
      const userIdValue = userData.userId || userData.user_id || userData.id;
      if (userIdValue) {
        localStorage.setItem('userId', userIdValue.toString());
      }
      
      // Update state
      setToken(newToken);
      setUser(userData);
      setIsAuthenticated(true);

      primeBusinessName();

      return data;
    } catch (error) {
      console.error('❌ Google Sign-In error:', error);
      throw error;
    }
  }, []);

  // Check if user has specific role
  const hasRole = useCallback((roleId) => {
    return user?.roleId === roleId;
  }, [user]);

  // Context value
  const value = {
    // State
    user,
    setUser,
    token,
    businessId,
    isAuthenticated,
    isLoading,

    // Actions
    login,
    logout,
    googleSignIn,
    getAuthHeaders,
    hasRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Export AuthContext as named export too for convenience
export { AuthContext };

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Higher-order component for protected routes
export const withAuth = (WrappedComponent) => {
  return function AuthenticatedComponent(props) {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
      return <div className="loading-spinner">Loading...</div>;
    }

    if (!isAuthenticated) {
      return <div className="auth-required">Please log in to access this page.</div>;
    }

    return <WrappedComponent {...props} />;
  };
};

export default AuthContext;